import type { StoreApi, UseBoundStore } from 'zustand';
import type {
  DriverState,
  WalkthroughManagerGetType,
  WalkthroughManagerSetType,
  WalkthroughManagerStore,
} from './types';

export interface DriverSlice {
  driverState: DriverState;
  driverActions: {
    setState: <K extends keyof DriverState>(key: K, value: DriverState[K]) => void;
    drive: (stepIndex?: number) => void;
    move: (direction: 'next' | 'previous') => void;
    destroy: () => void;
    setTooltipTransitionState: (state: DriverState['tooltipTransitionState']) => void;
    commitDisplayIndex: () => void;
  };
}

export const createDriverSlice = (
  set: WalkthroughManagerSetType,
  get: WalkthroughManagerGetType,
) => {
  const driverSlice: DriverSlice = {
    driverState: {
      steps: null,
      tooltipTransitionState: {
        status: 'idle',
        retryCount: 0,
        maxRetries: 3,
        targetStepIndex: -1,
      },
    },

    driverActions: {
      setState: <K extends keyof DriverState>(key: K, value: DriverState[K]) =>
        set((state) => ({
          driverState: { ...state.driverState, [key]: value },
        })),

      drive: (stepIndex = 0) => {
        const { driverState } = get();
        const { steps, displayIndex } = driverState;
        if (!steps) {
          console.error('No steps to drive through');
          get().driverActions.destroy();
          return;
        }

        if (!steps[stepIndex]) {
          get().driverActions.destroy();
          return;
        }

        const isInitialDisplay = typeof displayIndex === 'undefined';
        const safeDisplayIndex =
          !isInitialDisplay && typeof displayIndex === 'number' && steps[displayIndex]
            ? displayIndex
            : stepIndex;

        const _driverState: DriverState = {
          ...driverState,
          activeIndex: stepIndex,
          displayIndex: safeDisplayIndex, // keep rendering previous step until commitDisplayIndex
          __previousStep: steps[stepIndex - 1],
          __activeStep: steps[stepIndex],
          __displayStep: steps[safeDisplayIndex],
        };

        set({
          driverState: _driverState,
        });
      },

      move: (direction: 'next' | 'previous') => {
        const { driverState } = get();
        const { steps, activeIndex } = driverState;

        if (typeof activeIndex === 'undefined') {
          return;
        }

        const nextStepIndex = activeIndex + (direction === 'next' ? 1 : -1);
        const nextStep = steps?.[nextStepIndex];

        if (nextStep) {
          get().driverActions.drive(nextStepIndex);
        } else {
          get().driverActions.destroy();
        }
      },

      destroy: () => {
        set(() => ({
          driverState: {
            steps: null,
            tooltipTransitionState: {
              status: 'idle',
              retryCount: 0,
              maxRetries: 3,
              targetStepIndex: -1,
            },
          },
        }));
      },

      setTooltipTransitionState: (state: DriverState['tooltipTransitionState']) => {
        set((s) => ({
          driverState: { ...s.driverState, tooltipTransitionState: state },
        }));
      },

      commitDisplayIndex: () => {
        // Move displayIndex to activeIndex when element is ready
        const { driverState } = get();
        const { activeIndex, steps } = driverState;

        set({
          driverState: {
            ...driverState,
            displayIndex: activeIndex,
            __displayStep: steps?.[activeIndex ?? 0],
            tooltipTransitionState: {
              status: 'idle',
              retryCount: 0,
              maxRetries: driverState.tooltipTransitionState?.maxRetries ?? 3,
              targetStepIndex: activeIndex ?? -1,
            },
          },
        });
      },
    },
  };

  return driverSlice;
};
export const driverSelectors = (store: UseBoundStore<StoreApi<WalkthroughManagerStore>>) => ({
  useActiveStepIndex: () => store((state) => state.driverState.activeIndex),
  useDisplayStepIndex: () => store((state) => state.driverState.displayIndex),
  useIsFirstStep: () => store((state) => state.driverState.activeIndex === 0),
  useIsLastStep: () =>
    store((state) => {
      const steps = state.driverState.steps || [];
      const activeIndex = state.driverState.activeIndex;
      return activeIndex !== undefined && activeIndex === steps.length - 1;
    }),
  useActiveStep: () => store((state) => state.driverState.__activeStep),
  useDisplayStep: () => store((state) => state.driverState.__displayStep),
  usePreviousStep: () => store((state) => state.driverState.__previousStep),
  useNextStep: () =>
    store((state) => {
      const steps = state.driverState.steps || [];
      const activeIndex = state.driverState.activeIndex;
      return activeIndex !== undefined ? steps[activeIndex + 1] : undefined;
    }),
  useHasNextStep: () =>
    store((state) => {
      const steps = state.driverState.steps || [];
      const activeIndex = state.driverState.activeIndex;
      return activeIndex !== undefined && Boolean(steps[activeIndex + 1]);
    }),
  useHasPreviousStep: () =>
    store((state) => {
      const steps = state.driverState.steps || [];
      const activeIndex = state.driverState.activeIndex;
      return activeIndex !== undefined && Boolean(steps[activeIndex - 1]);
    }),
  useTooltipTransitionState: () => store((state) => state.driverState.tooltipTransitionState),
  useSteps: () => store((state) => state.driverState.steps),
  useDriverActions: () => store((state) => state.driverActions),
});

export const DriverHelper = {
  steps: (getState: WalkthroughManagerGetType) => {
    const state = getState();
    return state.driverState.steps;
  },

  getActiveStepIndex: (getState: WalkthroughManagerGetType) => {
    const state = getState();
    return state.driverState.activeIndex;
  },

  isFirstStep: (getState: WalkthroughManagerGetType) => {
    const state = getState();
    return state.driverState.activeIndex === 0;
  },

  isLastStep: (getState: WalkthroughManagerGetType) => {
    const state = getState();
    const steps = state.driverState.steps || [];
    const activeIndex = state.driverState.activeIndex;

    return activeIndex !== undefined && activeIndex === steps.length - 1;
  },

  getActiveStep: (getState: WalkthroughManagerGetType) => {
    const state = getState();
    return state.driverState.__activeStep;
  },

  getPreviousStep: (getState: WalkthroughManagerGetType) => {
    const state = getState();
    return state.driverState.__previousStep;
  },

  getNextStep: (getState: WalkthroughManagerGetType) => {
    const state = getState();
    const steps = state.driverState.steps || [];
    const activeIndex = state.driverState.activeIndex;

    return activeIndex !== undefined ? steps[activeIndex + 1] : undefined;
  },

  hasNextStep: (getState: WalkthroughManagerGetType) => {
    const state = getState();
    const steps = state.driverState.steps || [];
    const activeIndex = state.driverState.activeIndex;

    return activeIndex !== undefined && Boolean(steps[activeIndex + 1]);
  },

  hasPreviousStep: (getState: WalkthroughManagerGetType) => {
    const state = getState();
    const steps = state.driverState.steps || [];
    const activeIndex = state.driverState.activeIndex;

    return activeIndex !== undefined && Boolean(steps[activeIndex - 1]);
  },
};
